import { useState, useCallback, useEffect } from 'react';
import type { UseSequenceHUDStateReturn } from './sequenceHUD.types';

const STORAGE_KEY_SCALE = 'ui_scale';
const LEGACY_STORAGE_KEY_SCALE = 'about_ui_scale';
const STORAGE_KEY_RECRUITER = 'recruiter_mode_active';

const MIN_SCALE = 0.65;
const MAX_SCALE = 1.25;
const SCALE_STEP = 0.05;

/**
 * Global HUD state hook coordinating unified viewport zoom scale and recruiter mode toggle.
 * Syncs seamlessly across all 5 portfolio sections (Home, About, Projects, Experience, Achievements).
 */
export const useSequenceHUDState = (
  initialIsMobile = false,
  onRecruiterChange?: (isActive: boolean) => void
): UseSequenceHUDStateReturn => {
  // * Unified UI Zoom scale factor with localStorage persistence & responsive fallback
  const [scale, setScale] = useState<number>(() => {
    try {
      if (typeof window !== 'undefined') {
        const saved =
          localStorage.getItem(STORAGE_KEY_SCALE) ||
          localStorage.getItem(LEGACY_STORAGE_KEY_SCALE);
        if (saved) {
          const parsed = parseFloat(saved);
          if (!isNaN(parsed) && parsed >= MIN_SCALE && parsed <= MAX_SCALE) {
            return parsed;
          }
        }
        return window.innerWidth < 900 ? 0.85 : 1.0;
      }
    } catch (err) {
      console.error('[useSequenceHUDState] Failed to read cached UI scale:', err);
    }
    return initialIsMobile ? 0.85 : 1.0;
  });

  // * Recruiter Mode state — strictly defaults to false (OFF) on load
  const [isRecruiterActive, setIsRecruiterActive] = useState<boolean>(false);

  // * Ensure stale storage flags are cleared so recruiter mode stays off by default
  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        localStorage.removeItem(STORAGE_KEY_RECRUITER);
        sessionStorage.removeItem(STORAGE_KEY_RECRUITER);
      }
    } catch {
      // Fallback
    }
  }, []);

  // * Scale adjustment handlers (+ and -)
  const handleZoomIn = useCallback(() => {
    setScale((prev) => {
      const next = Math.min(
        MAX_SCALE,
        Math.round((prev + SCALE_STEP) * 100) / 100
      );
      try {
        localStorage.setItem(STORAGE_KEY_SCALE, String(next));
        localStorage.setItem(LEGACY_STORAGE_KEY_SCALE, String(next));
      } catch (err) {
        console.error('[useSequenceHUDState] Failed to persist scale:', err);
      }
      return next;
    });
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => {
      const next = Math.max(
        MIN_SCALE,
        Math.round((prev - SCALE_STEP) * 100) / 100
      );
      try {
        localStorage.setItem(STORAGE_KEY_SCALE, String(next));
        localStorage.setItem(LEGACY_STORAGE_KEY_SCALE, String(next));
      } catch (err) {
        console.error('[useSequenceHUDState] Failed to persist scale:', err);
      }
      return next;
    });
  }, []);

  // * Recruiter Mode click toggle handler
  const handleRecruiterToggle = useCallback(() => {
    setIsRecruiterActive((prev) => {
      const next = !prev;
      try {
        sessionStorage.setItem(STORAGE_KEY_RECRUITER, String(next));
        window.dispatchEvent(
          new CustomEvent('recruiter-mode-change', { detail: { active: next } })
        );
      } catch (err) {
        console.error('[useSequenceHUDState] Failed to persist recruiter mode:', err);
      }
      onRecruiterChange?.(next);
      return next;
    });
  }, [onRecruiterChange]);

  return {
    scale,
    isRecruiterActive,
    handleZoomIn,
    handleZoomOut,
    handleRecruiterToggle,
  };
};

export default useSequenceHUDState;
